import { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    removeTradeOrder,
    TradeOrder,
    updateBasketConditionalEntry,
    updateBasketConditionalExit,
    updateTradeOrder,
} from "../../../../reducer/strategyBuilder.reducer"
import { alpha, Box, Checkbox, Chip, IconButton, TableCell, TableRow, Tooltip, Typography } from "@mui/material"
import { useSelector } from "react-redux"
import { selectOrdersHistory } from "../../../../selectors/orders"
import TransactionTypeCell from "./transactionTypeCell"
import { Order, OrderStatus, TransactionType } from "../../../../proto/generated/order"
import { selectOptionsTradeData, selectUnderlyingOptionsTradeData } from "../../../../selectors/options"
import { selectUnderlyingFutureExpiries } from "../../../../selectors/futures"
import { getSecurityAsync, getSecurityKey } from "../../../../util/security"
import { createStringDateFromStamp } from "../../../../util/tradePage"
import ExpiryCell from "./expiryCell"
import { OptionType, SecurityCategory_CategoryCode } from "../../../../proto/generated/security"
import StrikeCell from "./strikeCell"
import SecurityTypeCell from "./securityTypeCell"
import QuantityCell from "./quantityCell"
import PriceCell from "./priceCell"
import { useStyles } from "../styles"
import { useDispatch } from "react-redux"
import { GetOptionChainRequestV2 } from "../../../../proto/generated/candle-data-depot-option-chain"
import { TimeBucket_TimeSeries } from "../../../../proto/generated/common"
import { fetchOptionChainV2 } from "../../../../util/options"
import { findNearestNumber } from "../../../../util/tradepanel"
import RemoveCell from "./removeCell"
import { selectBasketId, selectBatchId, selectIceberg, selectStrategyBuilderMode, selectWaitingForOrders } from "../../../../selectors/strategyBuilder"
import OrderStatusDisplay from "../../../orderStatus/orderStatusDisplay"
import { cancelOrder } from "../../../../services/apis/orders"
import { updateOrderHistory } from "../../../../reducer/orders.reducer"
import {
    effectiveOrderFilledQuantity,
    getCancellableIcebergLegIds,
    getIcebergLegOrdersForSecurity,
    getIcebergLegUiFromLegs,
    hasIncompleteIcebergOrdersInBatch,
    ICEBERG_NON_TERMINAL_STATUSES,
} from "../../../../util/icebergOrders"
import {
    batchHasAnyOpenPrimaryLeg,
    findCancellableConditionalAuxiliaryLegsInBatch,
} from "../../../../util/conditionalEntry"
import { TradingAccount } from "../../../../proto/generated/trading-bridge"
import { InfoOutlined } from "@mui/icons-material"
import store from "../../../../store"

export type TradeOrderRowProps = {
    order: TradeOrder,
    tradingAccount: TradingAccount,
    selectable?: boolean
}
const TradeOrderRow: React.FC<TradeOrderRowProps> = ({order, tradingAccount, selectable = true}) => {
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const orders = useSelector(selectOrdersHistory);
    const mode = useSelector(selectStrategyBuilderMode);
    const waitingForOrders = useSelector(selectWaitingForOrders);
    const batchId = useSelector(selectBatchId);
    const basketId = useSelector(selectBasketId);
    const iceberg = useSelector(selectIceberg);
    const underlyingToken = useMemo(() => getSecurityKey(order.security?.exchange, order.security?.underlyingSecurityCode) ,[order.security])
    const optionsTradeData = useSelector(selectOptionsTradeData);
    const { referenceOrderId, selected } = order;
    const icebergLegs = useMemo(
        () => getIcebergLegOrdersForSecurity(orders || [], batchId, basketId, order.security),
        [orders, batchId, basketId, order.security]
    );

    const referenceOrder = useMemo(() => orders?.find(o => o.orderId === referenceOrderId), [orders, referenceOrderId]);

    const effectiveReferenceOrder = useMemo(() => {
        if (icebergLegs.length > 0) {
            const pending = icebergLegs.find((l) =>
                ICEBERG_NON_TERMINAL_STATUSES.includes(l.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
            );
            return pending ?? icebergLegs[0];
        }
        return referenceOrder;
    }, [icebergLegs, referenceOrder]);

    const icebergLegsActive = icebergLegs.length > 0 && icebergLegs.some((l) =>
        ICEBERG_NON_TERMINAL_STATUSES.includes(l.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
    );

    const icebergFilledTotal = useMemo(() => {
        if (icebergLegs.length === 0) return null;
        const filled = icebergLegs.reduce((s, l) => s + effectiveOrderFilledQuantity(l), 0);
        const total = icebergLegs.reduce((s, l) => s + l.quantity, 0);
        return { filled, total };
    }, [icebergLegs]);

    const icebergLegUi = useMemo(
        () => getIcebergLegUiFromLegs(icebergLegs, iceberg.numberOfLegs),
        [icebergLegs, iceberg.numberOfLegs]
    );

    const optionsTradeDataRef = useRef(optionsTradeData ? optionsTradeData[underlyingToken] : null);

    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const futuresExpiries = useSelector(selectUnderlyingFutureExpiries(underlyingToken));

    const [displayName, setDisplayName] = useState<string>('')
    const orderSecurityKey = getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? '')
    useEffect(() => { if (orderSecurityKey) getSecurityAsync(orderSecurityKey).then(s => setDisplayName(s?.displayName ?? '')); else setDisplayName('') }, [orderSecurityKey])

    useEffect(() => {
        optionsTradeDataRef.current = optionsTradeData ? optionsTradeData[underlyingToken] : null;
    }, [optionsTradeData])

    const optionExpiriesMenuItems: {
            label: string
            value: number
        }[] = useMemo(() => {
            const filteredExpiries = (optionExpiries ?? []).filter(d => !d.derived)
            return filteredExpiries?.map(d => ({
                label: createStringDateFromStamp(Number(d.expiration.expiry?.expiry ?? 0)),
                value: d.expiration.expiry?.expiry ?? 0
            }))
        }, [optionExpiries])

    const futuresSecurityKeys = useMemo(() => (futuresExpiries?.current ?? []).filter((d: { derived?: boolean }) => !d.derived).map((d: { security?: { exchange?: number; code?: string } }) => getSecurityKey(d.security?.exchange ?? 0, d.security?.code ?? '')).filter(Boolean) as string[], [futuresExpiries])
    const [futuresSecurityMap, setFuturesSecurityMap] = useState<Map<string, Awaited<ReturnType<typeof getSecurityAsync>>>>(new Map())
    useEffect(() => {
        if (!futuresSecurityKeys.length) { setFuturesSecurityMap(new Map()); return }
        Promise.all(futuresSecurityKeys.map(k => getSecurityAsync(k))).then((secs) => {
            const m = new Map<string, Awaited<ReturnType<typeof getSecurityAsync>>>()
            futuresSecurityKeys.forEach((k, i) => { if (secs[i]) m.set(k, secs[i]!) })
            setFuturesSecurityMap(m)
        })
    }, [futuresSecurityKeys.join(',')])
    
    const futureExpiriesMenuItems: {
            label: string
            value: number
        }[] = useMemo(() => {
            const filteredExpiries = futuresExpiries.current.filter(d => !d.derived)
            const expiriesMenu = filteredExpiries?.map(d => {
                const sec = futuresSecurityMap.get(getSecurityKey(d.security?.exchange ?? 0, d.security?.code ?? ''))
                return {
                    label: createStringDateFromStamp(Number(sec?.expiry?.expiry ?? 0)),
                    value: sec?.expiry?.expiry ?? 0
                }
            })
            return expiriesMenu
        }, [futuresExpiries, futuresSecurityMap])

    const onSelectionToggle = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateTradeOrder({
            clientOrderId: order.clientOrderId,
            update: {
                selected: event.target.checked
            }
        }))
    }, [order])

    const onTransactionTypeChange = useCallback((transactionType: TransactionType) => {
        dispatch(updateTradeOrder({
            clientOrderId: order.clientOrderId,
            update: {
                transactionType
            }
        }))
    }, [order])


    const onOptionTypeChange = useCallback((optionType?: OptionType) => {
        if (!optionsTradeDataRef.current) return;

        const tradeData = optionsTradeDataRef.current;
        const expiry =  tradeData.find(t => 
            !t.derived && t.expiration.expiry.expiry === order.security.expiry.expiry);
        if (!expiry) return;
        const option = expiry.trades.find(t => t.strikeCol.strike === order.security.strikePrice)

        if (!option) return;

        let selectedOption = optionType === OptionType.CE ? option.callOption : option.putOption
        if (!selectedOption) return;
        dispatch(updateTradeOrder({
            clientOrderId: order.clientOrderId,
            update: {
                security: selectedOption.security,
                price:  order.transactionType === TransactionType.BUY ? selectedOption.topAsk : selectedOption.topBid
            },
        }))
    }, [order])

    const onExpiryChange = useCallback(async (newExpiry: number) => {

        if (order.security.categoryCode !== SecurityCategory_CategoryCode.FUTURES) {
            if (!optionsTradeDataRef.current) return;

            const tradeData = optionsTradeDataRef.current;
            const expiry =  tradeData.find(t => 
                !t.derived && t.expiration.expiry.expiry === newExpiry);
            if (!expiry) return;

            let trades = expiry.trades;

            if (!trades.length) {
                const underlying = await getSecurityAsync(underlyingToken);
                const payload: GetOptionChainRequestV2 = {
                    security: { code: underlying.code, exchange: underlying.exchange },
                    expiries: [newExpiry],
                    noOfStrikes: -1,
                    requestedFields: [],
                    timeBucket: {
                        timeSeries: TimeBucket_TimeSeries.DAY,
                        value: 1
                    },
                    sendFutures: true
                }
                const {optionsData} = await fetchOptionChainV2(payload, dispatch, optionsTradeDataRef.current, 'both', false)
                trades = optionsData.find(o => o.expiration.expiry.expiry === newExpiry)?.trades;
            }
            const option = trades.find(t => t.strikeCol.strike === order.security.strikePrice)
            if (!option) return;

            let selectedOption = order.security.optionType === OptionType.CE ? option.callOption : option.putOption
            if (!selectedOption) return;
            dispatch(updateTradeOrder({
                clientOrderId: order.clientOrderId,
                update: {
                    security: selectedOption.security,
                    price:  order.transactionType === TransactionType.BUY ? selectedOption.topAsk : selectedOption.topBid
                },
            }))
        }
    }, [order, underlyingToken])


    const onStrikeChange = useCallback((strike: number) => {
        if (!optionsTradeDataRef.current) return;

        const tradeData = optionsTradeDataRef.current;
        const expiry =  tradeData.find(t => 
            !t.derived && t.expiration.expiry.expiry === order.security.expiry.expiry);
        if (!expiry) return;
        let option = expiry.trades.find(t => t.strikeCol.strike === strike);
        if (!option) {
            const strikesMenu = expiry.trades.map(t => t.strikeCol.strike)
            const nearestStrike = findNearestNumber(strikesMenu ?? [], strike * (order.security?.priceMultiplier ?? 100))
            option = expiry.trades?.find(t => t.strikeCol?.strike === nearestStrike)
            if (!option) return;
        }
        

        let selectedOption = order.security.optionType === OptionType.CE ? option.callOption : option.putOption
        if (!selectedOption) return;

        if (selectedOption.security.code === order.security.code) {

            // to trigger change hook in strike cell
            dispatch(updateTradeOrder({
                clientOrderId: order.clientOrderId,
                update: {
                    security: {
                        ...(selectedOption.security),
                        strikePrice: 0
                    },
                    price:  order.transactionType === TransactionType.BUY ? selectedOption.topAsk : selectedOption.topBid
                },
            }))

            setTimeout(() => {
                dispatch(updateTradeOrder({
                    clientOrderId: order.clientOrderId,
                    update: {
                        security: selectedOption.security,
                        price:  order.transactionType === TransactionType.BUY ? selectedOption.topAsk : selectedOption.topBid
                    },
                }))
            }, 10)

            return;
        }

        dispatch(updateTradeOrder({
            clientOrderId: order.clientOrderId,
            update: {
                security: selectedOption.security,
                price:  order.transactionType === TransactionType.BUY ? selectedOption.topAsk : selectedOption.topBid
            },
        }))
    }, [order])

    const onStrikeDecrease = useCallback(() => {
        if (!optionsTradeDataRef.current) return;

        const tradeData = optionsTradeDataRef.current;
        const expiry =  tradeData.find(t => 
            !t.derived && t.expiration.expiry.expiry === order.security.expiry.expiry);
        if (!expiry) return;
        const trades = [...expiry.trades].sort((a, b) => a.strikeCol.strike - b.strikeCol.strike);
        const index = trades.findIndex(t => t.strikeCol.strike === order.security.strikePrice);
        const option = trades[index - 1];
        if (!option) return;

        let selectedOption = order.security.optionType === OptionType.CE ? option.callOption : option.putOption
        if (!selectedOption) return;
        dispatch(updateTradeOrder({
            clientOrderId: order.clientOrderId,
            update: {
                security: selectedOption.security,
                price:  order.transactionType === TransactionType.BUY ? selectedOption.topAsk : selectedOption.topBid
            },
        }))
    }, [order])
    
    const onStrikeIncrease = useCallback(() => {
        if (!optionsTradeDataRef.current) return;

        const tradeData = optionsTradeDataRef.current;
        const expiry =  tradeData.find(t => 
            !t.derived && t.expiration.expiry.expiry === order.security.expiry.expiry);
        if (!expiry) return;
        const trades = [...expiry.trades].sort((a, b) => a.strikeCol.strike - b.strikeCol.strike);
        const index = trades.findIndex(t => t.strikeCol.strike === order.security.strikePrice);
        const option = trades[index + 1];
        if (!option) return;

        let selectedOption = order.security.optionType === OptionType.CE ? option.callOption : option.putOption
        if (!selectedOption) return;
        dispatch(updateTradeOrder({
            clientOrderId: order.clientOrderId,
            update: {
                security: selectedOption.security,
                price:  order.transactionType === TransactionType.BUY ? selectedOption.topAsk : selectedOption.topBid
            },
        }))
    }, [order])

    const onRemove = useCallback(async (clientOrderId: string) => {
        if (!tradingAccount) return;
        const cancelIds =
            icebergLegs.length > 0
                ? getCancellableIcebergLegIds(icebergLegs)
                : referenceOrder &&
                    ![OrderStatus.REJECTED, OrderStatus.CANCELLED, OrderStatus.FAILED, OrderStatus.EXECUTED].includes(
                        referenceOrder.status
                    )
                  ? [referenceOrder.orderId!]
                  : [];
        if (cancelIds.length > 0) {
            const res = await cancelOrder({
                dispatch,
                body: {
                    orderId: cancelIds,
                    tradingAccountId: tradingAccount.accountId
                },
                notify: false
            });
            const updates: Order[] = [];
            const cancellableById = new Map(
                (icebergLegs.length > 0 ? icebergLegs : referenceOrder ? [referenceOrder] : [])
                    .filter((o): o is Order & { orderId: string } => Boolean(o.orderId))
                    .map((o) => [o.orderId, o])
            );
            res.orderResponse.forEach((responseOrder) => {
                if (!responseOrder?.successful || !responseOrder.commitOrder) return;
                const id = responseOrder.order?.orderId || responseOrder.orderId;
                const base = id ? cancellableById.get(id) : undefined;
                if (base) {
                    updates.push({ ...base, status: OrderStatus.CANCELLED });
                }
            });
            if (updates.length) {
                dispatch(updateOrderHistory({ updates }));
            }
        }
        dispatch(removeTradeOrder(clientOrderId));

        if (waitingForOrders && basketId && batchId && mode === "trade") {
            const history = store.getState().ordersStore.ordersHistory ?? []
            if (!batchHasAnyOpenPrimaryLeg(history, basketId, batchId)) {
                const aux = findCancellableConditionalAuxiliaryLegsInBatch(history, basketId, batchId)
                const auxIds = aux.map((o) => o.orderId!).filter(Boolean)
                if (auxIds.length > 0) {
                    const auxById = new Map(aux.map((o) => [o.orderId!, o]))
                    const resAux = await cancelOrder({
                        dispatch,
                        body: {
                            orderId: auxIds,
                            tradingAccountId: tradingAccount.accountId,
                        },
                        notify: false,
                    })
                    const auxUpdates: Order[] = []
                    resAux.orderResponse.forEach((responseOrder) => {
                        if (!responseOrder?.successful || !responseOrder.commitOrder) return
                        const id = responseOrder.order?.orderId || responseOrder.orderId
                        const base = id ? auxById.get(id) : undefined
                        if (base) {
                            auxUpdates.push({ ...base, status: OrderStatus.CANCELLED })
                        }
                    })
                    if (auxUpdates.length) {
                        dispatch(updateOrderHistory({ updates: auxUpdates }))
                    }
                    dispatch(updateBasketConditionalEntry({ enabled: false, condition: undefined }))
                    dispatch(updateBasketConditionalExit({ enabled: false, condition: undefined }))
                }
            }
        }
    }, [referenceOrder, tradingAccount, icebergLegs, dispatch, waitingForOrders, basketId, batchId, mode])

    const onQuantityChange = useCallback((quantity: number) => {
        const lotSize = order.security?.lotSize??1;
        let newQuantity = quantity;

        if (newQuantity <= 0) {
            newQuantity = lotSize
        } else if (newQuantity > order.security?.volumeFreezeQuantity) {
            newQuantity = Number(Math.floor((order.security?.volumeFreezeQuantity)/lotSize)) * lotSize;
        } else {
            newQuantity = Math.max(Math.floor(quantity/lotSize)*lotSize, lotSize)
        }

        if (newQuantity === order.quantity) {
            // to trigger change in quantity cell
            dispatch(updateTradeOrder({
                clientOrderId: order.clientOrderId,
                update: {
                    quantity: 0,
                },
            }));

            
            setTimeout(() => {
                dispatch(updateTradeOrder({
                    clientOrderId: order.clientOrderId,
                    update: {
                        quantity: newQuantity,
                    },
                }))
            }, 10)
            return;
        }
        dispatch(updateTradeOrder({
            clientOrderId: order.clientOrderId,
            update: {
                quantity: newQuantity,
            },
        }))
    }, [order])

    const onPriceChange = useCallback((price: number) => {
        if (!price || price <= 0) return;

        if (price === order.price) {
            // to trigger change in price cell
            dispatch(updateTradeOrder({
                clientOrderId: order.clientOrderId,
                update: {
                    price: 0,
                },
            }));

            
            setTimeout(() => {
                dispatch(updateTradeOrder({
                    clientOrderId: order.clientOrderId,
                    update: {
                        price,
                    },
                }))
            }, 10)
            return;
        }
        dispatch(updateTradeOrder({
            clientOrderId: order.clientOrderId,
            update: {
                price
            },
        }));
    }, [order])

    const securityEditable = useMemo(() => {
        if (icebergLegsActive) {
            return false;
        }
        return (
            (["trade"].includes(mode) && !waitingForOrders) ||
            !effectiveReferenceOrder ||
            ![
                OrderStatus.PENDING,
                OrderStatus.OPEN,
                OrderStatus.INACTIVE,
                OrderStatus.PARTIALLY_FILLED,
                OrderStatus.EXECUTED,
                OrderStatus.CANCELLED
            ].includes(effectiveReferenceOrder?.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
        );
    }, [effectiveReferenceOrder, icebergLegsActive, mode, waitingForOrders])
    
    const orderSelectable = useMemo(() => {
        return selectable && ((["trade"].includes(mode) && !waitingForOrders) || !effectiveReferenceOrder);
    }, [effectiveReferenceOrder, selectable, waitingForOrders])

    const quantityEditable = useMemo(
        () => icebergLegsActive ? false : ![OrderStatus.EXECUTED].includes(effectiveReferenceOrder?.status ?? OrderStatus.ORDER_STATUS_UNKNOWN),
        [effectiveReferenceOrder, icebergLegsActive]
    );

    const priceEditable = useMemo(
        () => ![OrderStatus.EXECUTED].includes(effectiveReferenceOrder?.status ?? OrderStatus.ORDER_STATUS_UNKNOWN),
        [effectiveReferenceOrder]
    );

    const orderRejected = useMemo(() => effectiveReferenceOrder?.status === OrderStatus.REJECTED, [effectiveReferenceOrder]);
    const orderFailed = useMemo(() => effectiveReferenceOrder?.status === OrderStatus.FAILED, [effectiveReferenceOrder]);

    const icebergBatchInFlight = useMemo(
        () => hasIncompleteIcebergOrdersInBatch(orders || [], batchId, basketId),
        [orders, batchId, basketId]
    );

    const showRemoveCell = useMemo(() => {
        if (iceberg.enabled && (waitingForOrders || icebergBatchInFlight)) {
            return false;
        }
        if (icebergLegs.length > 0) {
            return icebergLegs.some((l) => l.status !== OrderStatus.EXECUTED);
        }
        return (
            !effectiveReferenceOrder ||
            ![OrderStatus.EXECUTED].includes(effectiveReferenceOrder?.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
        );
    }, [iceberg.enabled, waitingForOrders, icebergBatchInFlight, icebergLegs, effectiveReferenceOrder]);

    const showIcebergPerLegQtyHint =
        iceberg.enabled && mode === "trade" && !icebergLegsActive && icebergLegs.length === 0 && quantityEditable;
    const needsExtraQtyRowPadding = Boolean(order.security && showIcebergPerLegQtyHint);
    return (
        <>
                <TableRow
                    className={classes.tableRow}
                    sx={
                        needsExtraQtyRowPadding
                            ? {
                                  height: showIcebergPerLegQtyHint ? "45px !important" : "auto",
                                  "& > .MuiTableCell-root": {
                                      verticalAlign: "top",
                                      paddingTop: "6px",
                                      paddingBottom: "6px",
                                  },
                              }
                            : undefined
                    }
                >
                {selectable && <TableCell className={classes.tableCell}>
                        {orderSelectable && <Checkbox checked={order.selected} onChange={onSelectionToggle} size="small" />}
                    </TableCell>}
                <TableCell className={classes.tableCell}>
                    <TransactionTypeCell transactionType={order.transactionType} onChange={onTransactionTypeChange} editable={securityEditable} disabled={!selected} />
                </TableCell>
                <TableCell className={classes.tableCell} colSpan={securityEditable ? 1 : 2}>
                    {securityEditable ? (order.security && <ExpiryCell security={order.security} disabled={!selected}
                        expiries={order.security.categoryCode === SecurityCategory_CategoryCode.FUTURES ? futureExpiriesMenuItems : optionExpiriesMenuItems}
                        onChange={onExpiryChange}
                    />) : <Typography fontSize='12px'>{displayName}</Typography> }
                </TableCell>
                {securityEditable && <TableCell className={classes.tableCell}>
                    {order.security && <StrikeCell security={order.security} disabled={!selected} onChange={onStrikeChange} onStrikeDecrease={onStrikeDecrease} onStrikeIncrease={onStrikeIncrease} /> }
                </TableCell>}
                <TableCell className={classes.tableCell}>
                    {order.security && (securityEditable ?
                        <SecurityTypeCell security={order.security} onChange={onOptionTypeChange} disabled={!selected || !securityEditable} />
                        : <OrderStatusDisplay status={effectiveReferenceOrder?.status} />
                    )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {order.security && (icebergLegs.length > 0 && icebergFilledTotal && icebergLegUi ? (
                        <Typography fontSize="12px" className="font-feature-num" sx={{ textAlign: "center", width: "100%" }}>
                            {icebergLegUi.allDone
                                ? `${icebergFilledTotal.filled}/${icebergFilledTotal.total}`
                                : `${icebergLegUi.filled}/${icebergLegUi.legQty}`}
                        </Typography>
                    ) : quantityEditable ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                width: "100%",
                                overflow: "visible",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "fit-content",
                                    display: "inline-block",
                                }}
                            >
                                <QuantityCell
                                    security={order.security}
                                    quantity={order.quantity}
                                    onChange={onQuantityChange}
                                    disabled={!selected}
                                />
                                {showIcebergPerLegQtyHint ? (
                                    <Chip
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: "100%",
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            mt: "2px",
                                            height: 13,
                                            width: "max-content",
                                            maxWidth: "none",
                                            border: "none",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: (theme) =>
                                            theme.palette.mode === "dark"
                                            ? theme.palette.grey[800]   // or darkHoverBtnColor from theme
                                            : theme.palette.grey[200],
                                            "& .MuiChip-label": {
                                                px: "6px",
                                                py: 0,
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                overflow: "visible",
                                                maxWidth: "none",
                                                whiteSpace: "nowrap",
                                                color: "inherit"
                                            },
                                            borderRadius: "8px",
                                        }}
                                        label={
                                            <Typography
                                                variant="caption"
                                                component="span"
                                                sx={{
                                                    fontSize: "8.5px",
                                                    color: "inherit",
                                                    lineHeight: 1,
                                                    letterSpacing: "-0.01em",
                                                    whiteSpace: "nowrap",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    m: 0,
                                                }}
                                            >
                                                {order.quantity} x {Math.max(1, iceberg.numberOfLegs)} ={" "}
                                                {order.quantity * Math.max(1, iceberg.numberOfLegs)}
                                            </Typography>
                                        }
                                    />
                                ) : null}
                            </Box>
                        </Box>
                    ) : (
                        <Typography fontSize="12px">{order.quantity}</Typography>
                    ))}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {order.security && (priceEditable ? (
                        <PriceCell
                            security={order.security}
                            price={order.price}
                            disabled={!selected}
                            transactionType={order.transactionType}
                            onChange={onPriceChange}
                        />
                    ) : (
                        <Typography fontSize="12px">
                            {(
                                (effectiveReferenceOrder?.averageTradedPrice || order.price) /
                                (order.security?.priceMultiplier ?? 100)
                            ).toFixed(2)}
                        </Typography>
                    ))}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {showRemoveCell ? <RemoveCell clientOrderId={order.clientOrderId} onRemove={onRemove} /> : null}
                </TableCell>
            </TableRow>
            {(orderRejected || orderFailed) && <TableRow>
                <TableCell className={classes.tableCell} colSpan={selectable ? 9 : 8} align="left">
                    <Typography fontSize='12px' color='error' textAlign="left" display="flex" alignItems="center">
                        Order {orderRejected ? 'Rejected' : 'Failed'}
                        { effectiveReferenceOrder?.message ? 
                            <Tooltip title={<Typography fontSize="14px">{effectiveReferenceOrder?.message}</Typography>} arrow>
                            <IconButton size="small" disableRipple sx={{ height: 16, width: 16, ml: 0.5, background: (theme) => alpha(theme.palette.background.paper, 0.7) }}>
                                <InfoOutlined sx={{ height: 16, width: 16 }} />
                            </IconButton>
                        </Tooltip>
                        : null }
                    </Typography>
                </TableCell>
            </TableRow>}
        </>
    );
};

export default memo(TradeOrderRow);